/**
 * Le message d'erreur à montrer, dans la langue de l'interface.
 *
 * ⚠️ Le serveur répond en français (anciennes routes) ou en anglais (routes à
 * contrat, `PERMISSION_DENIED`…) : ses messages sont écrits en dur. Les
 * afficher tels quels met une autre langue dans l'interface, ce qu'un
 * utilisateur anglophone a constaté le 2026-09-30.
 *
 * Le code d'erreur, lui, est stable et neutre. On traduit par le code
 * (`common:errors.<CODE>`, interpolé avec le `context` du serveur). Pour un
 * code qu'on ne connaît pas, on montre le message générique traduit de
 * l'écran (`fallbackKey`) ; l'appelant affiche le code à côté (DES-16), ou
 * utilise `errorToastOptions`. Le texte du serveur ne sert plus que quand il
 * n'y a pas de code du tout.
 *
 * QA-W7-1 : i18next rend `errors.X` (sans l'espace de noms) pour une clé
 * absente ; comparer à `common:errors.X` ne voyait donc jamais l'absence et
 * la clé brute s'affichait. On détecte l'absence par une valeur par défaut
 * sentinelle (équivalent de `i18n.exists`, et fonctionne avec un `t` injecté).
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Two body shapes come back: the flat { error: 'text', code } of AppError, and
// the structured { error: { code, message, context } } of DomainError. An
// ApiErrorException carries the structured one in `error`.
type ErrorBody = { code?: string; error?: string | { code?: string; message?: string; context?: Record<string, unknown> } };

export function errorCodeOf(err: unknown): string {
  const e = err as { data?: ErrorBody; code?: string } | undefined;
  const nested = typeof e?.data?.error === 'object' ? e.data.error?.code : undefined;
  return e?.data?.code || nested || e?.code || '';
}

export function errorContextOf(err: unknown): Record<string, unknown> {
  const e = err as { data?: ErrorBody; error?: unknown } | undefined;
  const fromData = typeof e?.data?.error === 'object' ? e.data.error?.context : undefined;
  const own = e?.error && typeof e.error === 'object' ? (e.error as { context?: unknown }).context : undefined;
  const ctx = fromData ?? own;
  return ctx && typeof ctx === 'object' ? (ctx as Record<string, unknown>) : {};
}

const MISSING = '\u0000missing\u0000';

/** The translation of `key`, or null when the catalogue has no entry for it. */
export function translateIfExists(t: Translate, key: string, options: Record<string, unknown> = {}): string | null {
  const out = t(key, { ...options, defaultValue: MISSING });
  if (!out || out === MISSING) return null;
  const bare = key.includes(':') ? key.slice(key.indexOf(':') + 1) : key;
  // A test double (or i18next without defaultValue support) returns the key.
  if (out === key || out === bare) return null;
  return out;
}

/** `records.write` → « Modifier les données » (admin:permissionsMatrix), else the raw name. */
export function permissionLabel(t: Translate, permission: unknown): string {
  if (typeof permission !== 'string' || !permission) return '';
  const id = permission.replace(/\./g, '_');
  return translateIfExists(t, `admin:permissionsMatrix.permissions.${id}.label`) ?? permission;
}

// Codes the API client invents for a body without one: no stable meaning, the
// server text is all there is.
const SYNTHETIC_CODES = new Set(['APP_ERROR', 'UNKNOWN']);

export function errorMessage(err: unknown, t: Translate, fallbackKey = 'common:errors.unknown'): string {
  const code = errorCodeOf(err);
  if (code && !SYNTHETIC_CODES.has(code)) {
    const context = errorContextOf(err);
    const translated = translateIfExists(t, `common:errors.${code}`, {
      ...context,
      permissionLabel: permissionLabel(t, context.permission),
      // QA-W7-9: an i18next context picks `<CODE>_<variant>` when it exists
      // (WEBHOOK_URL_BLOCKED_loopback…), else the plain `<CODE>`.
      ...(typeof context.address_class === 'string' ? { context: context.address_class } : {}),
    });
    if (translated) return translated;
    return translateIfExists(t, fallbackKey) ?? t('common:errors.unknownWithCode', { code });
  }
  const e = err as { data?: ErrorBody; message?: string } | undefined;
  const serverText = typeof e?.data?.error === 'object' ? e.data.error?.message : e?.data?.error;
  return serverText || e?.message || t(fallbackKey);
}

/** Sonner options that show the code under the message (DES-16), or nothing. */
export function errorToastOptions(err: unknown): { description: string } | undefined {
  const code = errorCodeOf(err);
  return code && !SYNTHETIC_CODES.has(code) ? { description: code } : undefined;
}
